import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAI } from '../../context/AIContext';
import { typography, surfaces, borders, textColors } from '../../../design-system/tokens';

/**
 * AIChatHistory - Modal dropdown for viewing all chats
 * Props:
 * - isOpen: boolean to show/hide dropdown
 * - onClose: function() called when dropdown closes
 * - websiteId: ID of current website to filter chats
 * - onSelectChat: function(chatId) called when a chat is selected
 * - anchorEl: DOM element to position dropdown relative to
 */
const AIChatHistory = ({
  isOpen,
  onClose,
  websiteId,
  onSelectChat,
  anchorEl
}) => {
  const { chats, fetchChats } = useAI();
  const dropdownRef = useRef(null);
  const [panelStyle, setPanelStyle] = useState(null);
  const normalizedWebsiteId =
    typeof websiteId === 'object'
      ? websiteId?.id || websiteId?.website_id || websiteId?.website?.id || websiteId?.value
      : websiteId;

  // Fetch chats when modal opens
  useEffect(() => {
    if (isOpen && normalizedWebsiteId) {
      fetchChats(normalizedWebsiteId, { includeArchived: true });
    }
  }, [isOpen, normalizedWebsiteId, fetchChats]);

  // Position the modal panel relative to the history button
  useEffect(() => {
    if (!isOpen || !anchorEl) return;
    const rect = anchorEl.getBoundingClientRect();
    const panelWidth = 320;
    const margin = 12;
    const top = Math.min(
      rect.bottom + 8,
      window.innerHeight - margin
    );
    const left = Math.min(
      Math.max(rect.right - panelWidth, margin),
      window.innerWidth - panelWidth - margin
    );
    setPanelStyle({ top, left, width: panelWidth });
  }, [isOpen, anchorEl]);

  // Group chats by date
  const groupedChats = useMemo(() => {
    const websiteChats = (Array.isArray(chats) ? chats : [])
      .filter(chat => {
        const chatWebsiteId = typeof chat.website === 'object' ? chat.website?.id : chat.website;
        return String(chatWebsiteId) === String(normalizedWebsiteId);
      })
      .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const thisWeek = new Date(today);
    thisWeek.setDate(thisWeek.getDate() - 7);
    const thisMonth = new Date(today);
    thisMonth.setMonth(thisMonth.getMonth() - 1);

    const groups = {
      today: [],
      yesterday: [],
      thisWeek: [],
      thisMonth: [],
      older: []
    };

    websiteChats.forEach(chat => {
      const chatDate = new Date(chat.updated_at);
      if (chatDate >= today) {
        groups.today.push(chat);
      } else if (chatDate >= yesterday) {
        groups.yesterday.push(chat);
      } else if (chatDate >= thisWeek) {
        groups.thisWeek.push(chat);
      } else if (chatDate >= thisMonth) {
        groups.thisMonth.push(chat);
      } else {
        groups.older.push(chat);
      }
    });

    return groups;
  }, [chats, normalizedWebsiteId]);

  // Handle ESC key
  useEffect(() => {
    if (!isOpen) return;

    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // Handle chat selection
  const handleSelectChat = (chatId) => {
    onSelectChat(chatId);
    onClose();
  };


  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  };

  // Chat item component
  const ChatItem = ({ chat }) => (
    <div
      className={`group flex items-start justify-between px-3 py-2 ${surfaces.hover} cursor-pointer transition-colors`}
      onClick={() => handleSelectChat(chat.id)}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <h4 className={`${typography.bodySmall} ${textColors.primary} truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors`}>
            {chat.title || 'Untitled Chat'}
          </h4>
          {chat.is_archived ? (
            <span className={`flex-shrink-0 rounded-full px-2 py-0.5 ${typography.caption} ${surfaces.secondary} ${textColors.secondary}`}>
              Hidden
            </span>
          ) : null}
        </div>
        <div className={`flex items-center gap-2 mt-0.5 ${typography.caption} ${textColors.tertiary}`}>
          <span>{chat.message_count || 0} messages</span>
          <span>·</span>
          <span>{formatDate(chat.updated_at)}</span>
        </div>
      </div>
    </div>
  );

  // Section component
  const Section = ({ title, chats }) => {
    if (chats.length === 0) return null;

    return (
      <div>
        <div className={`px-3 py-1.5 ${typography.overline} ${surfaces.secondary} ${textColors.tertiary}`}>
          {title}
        </div>
        <div>
          {chats.map(chat => (
            <ChatItem key={chat.id} chat={chat} />
          ))}
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  const totalChats = Object.values(groupedChats).reduce((sum, group) => sum + group.length, 0);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onClick={onClose}
        >
          <div className="absolute inset-0 bg-black dark:bg-dark-950 bg-opacity-50 dark:bg-opacity-70" />
          <motion.div
            ref={dropdownRef}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.15 }}
            className={`absolute ${surfaces.elevated} rounded-lg shadow-xl border ${borders.default} overflow-hidden`}
            style={panelStyle || { top: 60, right: 16, width: 320 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className={`px-3 py-2 border-b ${borders.default} ${surfaces.secondary}`}>
              <h3 className={`${typography.bodySmall} ${textColors.primary}`}>Chat History</h3>
            </div>

            {/* Content */}
            <div className="overflow-y-auto" style={{ maxHeight: "360px" }}>
              {totalChats === 0 ? (
                <div className="text-center py-8 px-4">
                  <p className={`${typography.bodySmall} ${textColors.tertiary}`}>No chats found</p>
                  <p className={`${typography.caption} ${textColors.muted} mt-1`}>Create a new chat to get started</p>
                </div>
              ) : (
                <>
                  <Section title="Today" chats={groupedChats.today} />
                  <Section title="Yesterday" chats={groupedChats.yesterday} />
                  <Section title="This Week" chats={groupedChats.thisWeek} />
                  <Section title="This Month" chats={groupedChats.thisMonth} />
                  <Section title="Older" chats={groupedChats.older} />
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AIChatHistory;

